export type ToolArgRow = {
  name: string;
  type: string;
  required: boolean;
  description: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function schemaType(spec: Record<string, unknown> | null): string {
  if (!spec) return 'any';
  const raw = spec.type;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (Array.isArray(raw) && raw.length) {
    return raw.map((item) => String(item)).join(' | ');
  }
  if (Array.isArray(spec.enum) && spec.enum.length) return 'enum';
  if (spec.items) return 'array';
  if (spec.properties) return 'object';
  if (Array.isArray(spec.anyOf) || Array.isArray(spec.oneOf)) return 'union';
  return 'any';
}

function placeholderForType(typeName: string, spec: Record<string, unknown> | null): unknown {
  if (spec && Array.isArray(spec.enum) && spec.enum.length) return spec.enum[0];
  if (typeName.includes('array')) return [];
  if (typeName.includes('object')) return {};
  if (typeName.includes('boolean')) return false;
  if (typeName.includes('integer') || typeName.includes('number')) return 0;
  if (typeName.includes('null') && typeName.includes('string')) return '';
  return '';
}

export function listToolArgs(schema: Record<string, unknown> | null | undefined): ToolArgRow[] {
  const root = asRecord(schema);
  if (!root) return [];
  const props = asRecord(root.properties);
  if (!props || !Object.keys(props).length) return [];
  const required = new Set(
    Array.isArray(root.required) ? root.required.filter((item): item is string => typeof item === 'string') : []
  );
  return Object.entries(props).map(([name, raw]) => {
    const spec = asRecord(raw);
    return {
      name,
      type: schemaType(spec),
      required: required.has(name),
      description: spec && typeof spec.description === 'string' ? spec.description : '',
    };
  });
}

export function schemaToExample(schema: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const root = asRecord(schema);
  if (!root) return {};
  const props = asRecord(root.properties);
  if (!props) return {};
  const required = Array.isArray(root.required)
    ? root.required.filter((item): item is string => typeof item === 'string')
    : [];
  const names = [...required, ...Object.keys(props).filter((key) => !required.includes(key))];
  const out: Record<string, unknown> = {};
  for (const name of names) {
    if (!(name in props) || name in out) continue;
    if (!required.includes(name)) continue;
    const spec = asRecord(props[name]);
    out[name] = placeholderForType(schemaType(spec), spec);
  }
  return out;
}

export function schemaToExampleJson(schema: Record<string, unknown> | null | undefined): string {
  return JSON.stringify(schemaToExample(schema), null, 2);
}
